// src/hooks/useDailyUpdates.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { logger } from '@/lib/logger';
import { formatLocalDate } from '@/lib/dateUtils';

// =====================================================
// QUERIES
// =====================================================

/**
 * Get daily updates for a specific date and group
 */
export const useDailyUpdates = (groupId, date, options = {}) => {
  return useQuery({
    queryKey: ['daily_updates', groupId, date],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('daily_updates')
        .select('*')
        .eq('group_id', groupId)
        .eq('date', date)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!groupId && !!date,
    ...options
  });
};

/**
 * Get daily updates for multiple groups (teacher with multiple groups)
 */
export const useMultiGroupDailyUpdates = (groupIds = [], date, options = {}) => {
  return useQuery({
    queryKey: ['daily_updates', 'multi', groupIds, date],
    queryFn: async () => {
      if (!groupIds || groupIds.length === 0) return [];

      const { data, error } = await supabase
        .from('daily_updates')
        .select('*')
        .in('group_id', groupIds)
        .eq('date', date)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: groupIds && groupIds.length > 0 && !!date,
    ...options
  });
};

/**
 * Get updates for specific child (parent view)
 */
export const useChildUpdates = (childId, startDate, endDate, options = {}) => {
  return useQuery({
    queryKey: ['daily_updates', 'child', childId, startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from('daily_updates')
        .select('*')
        .eq('child_id', childId)
        .order('date', { ascending: false });

      if (startDate) {
        query = query.gte('date', startDate);
      }
      if (endDate) {
        query = query.lte('date', endDate);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
    enabled: !!childId,
    ...options
  });
};

/**
 * Get today's update for a specific child
 */
export const useTodayChildUpdate = (childId, options = {}) => {
  const today = formatLocalDate(new Date());
  
  return useQuery({
    queryKey: ['daily_updates', 'child', childId, 'today'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('daily_updates')
        .select('*')
        .eq('child_id', childId)
        .eq('date', today)
        .maybeSingle();

      if (error) throw error;
      return data;
    },
    enabled: !!childId,
    ...options
  });
};

/**
 * Get weekly updates for children (parent view - shows rolling 7 days)
 */
export const useWeeklyChildrenUpdates = (childIds = [], options = {}) => {
  return useQuery({
    queryKey: ['daily_updates', 'weekly', childIds],
    queryFn: async () => {
      if (!childIds || childIds.length === 0) return [];

      const today = new Date();
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const startDate = formatLocalDate(weekAgo);

      const { data, error } = await supabase
        .from('daily_updates')
        .select('*')
        .in('child_id', childIds)
        .gte('date', startDate)
        .order('date', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: childIds && childIds.length > 0,
    ...options
  });
};

// =====================================================
// MUTATIONS
// =====================================================

/**
 * Create or update daily update for a child
 */
export const useUpsertDailyUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateData) => {
      // Check if update exists for this child and date
      const { data: existing } = await supabase
        .from('daily_updates')
        .select('id')
        .eq('child_id', updateData.child_id)
        .eq('date', updateData.date)
        .maybeSingle();

      let result;
      if (existing) {
        // Update existing
        const { data, error } = await supabase
          .from('daily_updates')
          .update(updateData)
          .eq('id', existing.id)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      } else {
        // Create new
        const { data, error } = await supabase
          .from('daily_updates')
          .insert(updateData)
          .select()
          .single();
        
        if (error) throw error;
        result = data;
      }

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily_updates'] });
    },
    onError: (error) => {
      logger.error('Update daily update error', { message: error.message });
      toast.error(`Failed to save: ${error.message}`);
    }
  });
};

/**
 * Bulk update multiple children at once (teacher efficiency)
 */
export const useBulkUpdateDailyUpdates = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updates) => {
      // Use upsert to handle existing records
      const { data, error } = await supabase
        .from('daily_updates')
        .upsert(updates, { 
          onConflict: 'child_id,date',
          ignoreDuplicates: false 
        })
        .select();

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['daily_updates'] });
      toast.success(`${data.length} updates saved!`);
    },
    onError: (error) => {
      logger.error('Bulk update error', { message: error.message });
      toast.error(`Failed to save updates: ${error.message}`);
    }
  });
};

/**
 * Delete a daily update
 */
export const useDeleteDailyUpdate = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (updateId) => {
      const { error } = await supabase
        .from('daily_updates')
        .delete()
        .eq('id', updateId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['daily_updates'] });
      toast.success('Update deleted');
    },
    onError: (error) => {
      logger.error('Delete update error', { message: error.message });
      toast.error(`Failed to delete: ${error.message}`);
    }
  });
};

/**
 * Get monthly evaluations for a child (parent view - for monthly summary)
 * Returns aggregated criteria values for the specified month
 */
export const useChildMonthlyEvaluations = (childId, year, month, options = {}) => {
  return useQuery({
    queryKey: ['daily_updates', 'monthly', childId, year, month],
    queryFn: async () => {
      // Calculate month start and end dates
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const endDate = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

      const { data, error } = await supabase
        .from('daily_updates')
        .select('date, criteria_values')
        .eq('child_id', childId)
        .gte('date', startDate)
        .lte('date', endDate)
        .order('date', { ascending: true });

      if (error) throw error;

      // Aggregate criteria values
      const aggregated = {};
      let totalDays = 0;

      (data || []).forEach(update => {
        if (!update.criteria_values) return;
        totalDays++;

        Object.entries(update.criteria_values).forEach(([key, value]) => {
          if (!aggregated[key]) {
            aggregated[key] = { positive: 0, neutral: 0, negative: 0, total: 0 };
          }
          if (value === 'positive' || value === 'neutral' || value === 'negative') {
            aggregated[key][value]++;
            aggregated[key].total++;
          }
        });
      });

      return {
        aggregated,
        totalDays,
        startDate,
        endDate
      };
    },
    enabled: !!childId && !!year && !!month,
    ...options
  });
};

/**
 * Get monthly evaluations for current and previous month (for trend calculation)
 */
export const useChildMonthlyTrend = (childId, year, month, options = {}) => {
  // Calculate previous month
  let prevYear = year;
  let prevMonth = month - 1;
  if (prevMonth < 1) {
    prevMonth = 12;
    prevYear = year - 1;
  }

  const currentMonth = useChildMonthlyEvaluations(childId, year, month, options);
  const previousMonth = useChildMonthlyEvaluations(childId, prevYear, prevMonth, options);

  return {
    current: currentMonth,
    previous: previousMonth,
    isLoading: currentMonth.isLoading || previousMonth.isLoading,
    isError: currentMonth.isError || previousMonth.isError
  };
};

/**
 * Get daily updates for summary (teacher view): by group(s) or by child, in date range.
 * Used by Podsumowanie section to aggregate criteria stats and tendencies.
 */
export const useSummaryRangeUpdates = (
  { groupIds = [], childId = null, startDate, endDate },
  options = {}
) => {
  const hasScope = (groupIds?.length > 0) || !!childId;
  const hasRange = !!startDate && !!endDate;

  return useQuery({
    queryKey: ['daily_updates', 'summary', groupIds, childId, startDate, endDate],
    queryFn: async () => {
      let query = supabase
        .from('daily_updates')
        .select('date, criteria_values, badges, child_id')
        .gte('date', startDate)
        .lte('date', endDate);

      if (childId) {
        query = query.eq('child_id', childId);
      } else if (groupIds?.length > 0) {
        query = query.in('group_id', groupIds);
      }

      const { data, error } = await query.order('date', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: hasScope && hasRange,
    ...options
  });
};
